import { useMemo } from 'react'

import { matchFilterArrayIncludes, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['orderItemTaskTypes']

type OrderItemTaskTypeFilters = {
  allowed_task_types?: string[],
}

type UseOrderItemTaskTypesOptions = {
  filters?: OrderItemTaskTypeFilters,
  performHttpRequests?: boolean,
}

function useOrderItemTaskTypes(options: UseOrderItemTaskTypesOptions = {}) {
  const { filters = {} } = options
  const {
    allowed_task_types: filterAllowedTaskTypes,
  } = filters

  const {
    updatedEntities: { orderItemTaskTypes: orderItemTaskTypesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { orderItemTaskTypes } = useSelector(reduxState => reduxState.entities)

  const filteredOrderItemTaskTypes = useMemo(() => {
    const filtered = Object.values(orderItemTaskTypes).filter((orderItemTaskType) => {
      const { short_name } = orderItemTaskType

      const matchAllowedTaskType = filterAllowedTaskTypes
        ? matchFilterArrayIncludes(filterAllowedTaskTypes, short_name)
        : true

      return matchAllowedTaskType
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [orderItemTaskTypesUpdatedAt, JSON.stringify(filters)])

  const filteredOrderItemTaskTypesCount = filteredOrderItemTaskTypes.length
  const hasFilteredOrderItemTaskTypes = !!filteredOrderItemTaskTypesCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredOrderItemTaskTypesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingOrderItemTaskTypes } = useReduxAction(
    'orderItemTaskTypes',
    'loadOrderItemTaskTypes',
    {
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredOrderItemTaskTypes,
    filteredOrderItemTaskTypesCount,
    hasFilteredOrderItemTaskTypes,
    loading: loadingOrderItemTaskTypes,
  }
}

export default useOrderItemTaskTypes
